import { Modal, Button, Form } from "react-bootstrap";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { STATENAMES } from "../constants/states";

// Keep track of the last used serial number across component instances
let lastSerialNumber = 119;

const getSubTypeCode = (subType) => {
  switch (subType) {
    case "inline":
      return "I";
    case "back_to_back":
      return "B2B";
    case "natural_draft":
      return "ND";
    case "package_tower":
      return "PT";
    case "single_flow":
      return "";
    case "double_flow":
      return "";
    default:
      return "";
  }
};

const EditNumber = ({
  show,
  onHide,
  setEdit,
  setSerialNumber,
  parentAnswers,
  setParentAnswers,
  initialState = "",
}) => {
  const [loading, setLoading] = useState(false);
  const [primaryState, setPrimaryState] = useState("");
  const [towerType, setTowerType] = useState("");
  const [subType, setSubType] = useState("");
  const [generatedSerial, setGeneratedSerial] = useState("");
  const location = useLocation();
  const [randomNumber, setRandomNumber] = useState("");

  useEffect(() => {
    if (
      location.state?.initialData?.towerInfo?.generalTowerDescription?.state
    ) {
      // Get state from location state and convert to abbreviation
      const fullStateName =
        location.state.initialData.towerInfo.generalTowerDescription.state;
      // Get abbreviation from STATENAMES (reverse lookup)
      const stateAbbr =
        Object.entries(STATENAMES).find(
          ([name, abbr]) => name.toLowerCase() === fullStateName.toLowerCase()
        )?.[1] || fullStateName;
      setPrimaryState(stateAbbr);
      setLoading(false);
    } else if (initialState) {
      // Fallback to passed initialState
      const stateAbbr = STATENAMES[initialState.toUpperCase()] || initialState;
      setPrimaryState(stateAbbr);
      setLoading(false);
    }
  }, [location.state, initialState, show]);

  useEffect(() => {
    updatePreview();
  }, [primaryState, towerType, subType]);

  const handleGenerate = () => {
    if (!generatedSerial) {
      return;
    }

    setSerialNumber(generatedSerial);
    setParentAnswers({
      ...parentAnswers,
      tower: {
        type: towerType,
        subtype: subType,
      },
      towerType: {
        tower_type: towerType,
        subType: subType,
      },
      towerSubtype: {
        tower_subtype: subType,
      },
      generalInfo: {
        ...parentAnswers.generalInfo,
        state: primaryState,
        serialNumber: generatedSerial,
      },
    });
    onHide();
  };
  const updatePreview = async () => {
    if (primaryState && towerType) {
      setLoading(true);
      const typeCode = towerType === "crossflow" ? "XF" : "CF";
      const subCode = subType ? getSubTypeCode(subType) : "";

      // Increment the last used serial number
      lastSerialNumber++;

      // Format the serial number with leading zeros
      const serialNumber = String(lastSerialNumber).padStart(7, "0");
      setRandomNumber(serialNumber);

      let preview = `${primaryState}-${typeCode}`;
      if (subCode) preview += subCode;
      if (subType) preview += `-${serialNumber}`;

      setGeneratedSerial(preview);
      setLoading(false);
    }
  };

  const getTowerTypeOptions = () => (
    <select
      className="form-select"
      value={towerType}
      onChange={(e) => {
        setTowerType(e.target.value);
        setSubType("");
      }}
    >
      <option value="">Select Tower Type</option>
      {[
        { value: "crossflow", label: "Crossflow" },
        { value: "counterflow", label: "Counterflow" },
      ].map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  const getSubTypeOptions = () => {
    const options =
      towerType === "crossflow"
        ? [
            { value: "single_flow", label: "Single Flow" },
            { value: "double_flow", label: "Double Flow" },
            { value: "package_tower", label: "Package Tower" },
            { value: "natural_draft", label: "Natural Draft" },
          ]
        : [
            { value: "inline", label: "Inline" },
            { value: "back_to_back", label: "Back to Back" },
            { value: "natural_draft", label: "Natural Draft" },
            { value: "package_tower", label: "Package Tower" },
          ];

    return (
      <select
        className="form-select"
        value={subType}
        onChange={(e) => setSubType(e.target.value)}
      >
        <option value="">Select Sub Type</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header>
        <Modal.Title>Generate Serial Number</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div>
            {generatedSerial && (
              <div className="mb-3">
                <label className="form-label">Generated Serial Number</label>
                <Form.Control
                  type="text"
                  value={generatedSerial}
                  disabled
                  className="bg-light"
                />
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>State</Form.Label>
              {location.state?.initialData ? (
                // If coming from opportunity, show disabled field
                <Form.Control
                  type="text"
                  value={primaryState}
                  disabled
                  className="bg-light"
                />
              ) : (
                // If standalone tower creation, show state selector
                <Form.Select
                  value={primaryState}
                  onChange={(e) => setPrimaryState(e.target.value)}
                >
                  <option value="">Select State</option>
                  {Object.entries(STATENAMES).map(([name, abbr]) => (
                    <option key={abbr} value={abbr}>
                      {abbr} - {name}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>

            <div className="mt-3">
              <label className="form-label">Tower Type</label>
              {getTowerTypeOptions()}
            </div>

            {towerType && (
              <div className="mt-3">
                <label className="form-label">Sub Type</label>
                {getSubTypeOptions()}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={!generatedSerial}
        >
          Use This Serial Number
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditNumber;
